// Keyboard shortcuts. The viewport takes no keys; every shortcut is here, and the help sheet
// (features/Help.tsx) lists the same table.

import { useEffect } from 'react';
import { useAtlas } from './store.ts';
import { SCENARIOS } from '../scenarios/index.ts';
import { timelineOf } from './scene.ts';
import { downloadCapture } from '../features/capture.ts';
import { startTour, tourGo } from '../features/Tour.tsx';

export interface Shortcut {
  keys: string[];
  label: string;
  group: 'Scenes' | 'View' | 'Selection' | 'Time';
}

export const SHORTCUTS: Shortcut[] = [
  { keys: ['1', '…', '8'], label: 'Switch scene', group: 'Scenes' },
  { keys: ['⌘', 'K'], label: 'Search', group: 'Scenes' },
  { keys: ['G'], label: 'Gallery: every shot and result', group: 'Scenes' },
  { keys: ['T'], label: 'Tour: the brain at every scale, step by step', group: 'Scenes' },
  { keys: ['⇧', 'T'], label: 'Tour this scene’s own cards, in order', group: 'Scenes' },
  { keys: ['?'], label: 'These shortcuts', group: 'Scenes' },
  { keys: ['C'], label: 'Capture an image', group: 'View' },
  { keys: ['P'], label: 'Presentation: hide every panel', group: 'View' },
  { keys: ['F'], label: 'Frame the selection, or everything', group: 'View' },
  { keys: ['R'], label: 'Reset the view', group: 'View' },
  { keys: ['L'], label: 'Labels on or off', group: 'View' },
  { keys: ['['], label: 'Left panel', group: 'View' },
  { keys: [']'], label: 'Inspector', group: 'View' },
  { keys: ['I'], label: 'Isolate the selection', group: 'Selection' },
  { keys: ['H'], label: 'Hide the selection', group: 'Selection' },
  { keys: ['⇧', 'H'], label: 'Show everything hidden', group: 'Selection' },
  { keys: ['Esc'], label: 'Close, deselect, then show all', group: 'Selection' },
  { keys: ['Space'], label: 'Play or pause', group: 'Time' },
  { keys: ['←', '→'], label: 'Step (⇧ for ten)', group: 'Time' },
  { keys: ['Home'], label: 'Back to the start', group: 'Time' },
];

// A focused slider owns its arrow keys: stepping the clot or the section plane must not also step time.
const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.getAttribute('role') === 'slider');

export function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = useAtlas.getState();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        s.setPalette(!s.paletteOpen);
        return;
      }
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (s.helpOpen) {
        if (e.key === 'Escape' || e.key === '?') {
          e.preventDefault();
          s.setHelp(false);
        }
        return;
      }
      if (s.galleryOpen) {
        if (e.key === 'Escape' || e.key === 'g' || e.key === 'G') {
          e.preventDefault();
          s.setGallery(false);
        }
        return;
      }
      if (s.paletteOpen) return;
      if (s.tour) {
        // The tour's own keys: Esc closes it; Enter moves on and ⇧Enter back, unless a button has the focus and its own Enter.
        if (e.key === 'Escape') {
          e.preventDefault();
          s.setTour(null);
          return;
        }
        if (e.key === 'Enter' && !(e.target instanceof HTMLElement && e.target.closest('button, a, [role="button"]'))) {
          e.preventDefault();
          tourGo(e.shiftKey ? -1 : 1);
          return;
        }
      }
      const tl = timelineOf(s);
      const t0 = tl?.start ?? 0;
      const step = tl ? (tl.duration - t0) / 100 : 0;
      const sc = SCENARIOS.find((x) => x.key === e.key);
      let handled = true;
      if (sc) s.setScenario(sc.id);
      else
        switch (e.key) {
          case '/':
            s.setPalette(true);
            break;
          case '?':
            s.setHelp(true);
            break;
          case 'g':
          case 'G':
            s.setGallery(true);
            break;
          case 't':
          case 'T':
            // T the written tour, ⇧T the scene's own (tours.ts sceneTour); either key ends a running one.
            if (s.tour) s.setTour(null);
            else startTour(e.shiftKey ? s.scenario : 'start');
            break;
          case 'f':
          case 'F':
            s.camera({ frame: s.selected ? { id: s.selected } : 'visible' });
            break;
          case 'r':
          case 'R':
            s.resetView();
            break;
          case 'l':
          case 'L':
            s.toggleLabels();
            break;
          case 'c':
          case 'C':
            void downloadCapture();
            break;
          case 'p':
          case 'P':
            s.setPresenting(!s.presenting);
            break;
          case '[':
            s.togglePanel('left');
            break;
          case ']':
            s.togglePanel('right');
            break;
          case 'i':
          case 'I':
            if (s.selected) s.isolateTarget(s.isolate && 'id' in s.isolate && s.isolate.id === s.selected ? null : { id: s.selected });
            break;
          case 'h':
            if (s.selected) {
              s.setOverride(s.selected, 'hidden');
              s.select(null);
            }
            break;
          case 'H':
            for (const [id, m] of Object.entries(s.overrides)) if (m === 'hidden') s.setOverride(id, null);
            break;
          case 'Escape':
            if (s.presenting) s.setPresenting(false);
            else if (s.methodsOpen) s.setMethods(false);
            else if (s.selected) s.select(null);
            else if (s.isolate) s.isolateTarget(null);
            break;
          case ' ':
            if (tl) s.setPlaying(!s.playing);
            break;
          case 'ArrowRight':
            if (tl) s.setTime(Math.min(tl.duration, s.time + (e.shiftKey ? step * 10 : step)));
            break;
          case 'ArrowLeft':
            if (tl) s.setTime(Math.max(t0, s.time - (e.shiftKey ? step * 10 : step)));
            break;
          case 'Home':
            if (tl) s.setTime(t0);
            break;
          default:
            handled = false;
        }
      if (handled) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
